import React, { useState, useRef, useCallback } from 'react';
import { 
  PaperClipIcon, 
  XMarkIcon, 
  DocumentIcon,
  PhotoIcon,
  ExclamationCircleIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import { 
  validateAttachments, 
  formatFileSize, 
  getFileExtension, 
  isImageFile,
  MAX_FILES,
  MAX_TOTAL_SIZE 
} from '../../../utils/attachmentValidation';

/**
 * FileAttachment component for handling file uploads in feedback forms
 */
const FileAttachment = ({ 
  files = [], 
  onChange, 
  error = null,
  disabled = false 
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFiles = useCallback((selectedFiles) => {
    const fileArray = Array.from(selectedFiles);
    const validation = validateAttachments(fileArray);
    
    setValidationResult(validation);
    
    if (validation.isValid) {
      onChange(fileArray);
    } else {
      // Only pass valid files if there are any
      if (validation.validFiles.length > 0) {
        onChange(validation.validFiles);
      }
    }
  }, [onChange]);

  // Handle file input change
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  // Remove a file
  const removeFile = (indexToRemove) => {
    const updatedFiles = files.filter((_, index) => index !== indexToRemove);
    onChange(updatedFiles);
    setValidationResult(null);
  };

  // Open file picker
  const openFilePicker = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  // Get file icon based on type
  const getFileIcon = (file) => {
    if (isImageFile(file.type)) {
      return <PhotoIcon className="w-5 h-5" />;
    }
    return <DocumentIcon className="w-5 h-5" />;
  };

  // Calculate total size
  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  const remainingSize = MAX_TOTAL_SIZE - totalSize;

  return (
    <div className="space-y-3">
      {/* Compact File Drop Zone */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-4 transition-all duration-200
          ${dragActive 
            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
            : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={openFilePicker}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".png,.jpg,.jpeg,.gif,.pdf,.txt,.log"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />
        
        <div className="flex items-center gap-3">
          <CloudArrowUpIcon className={`h-8 w-8 flex-shrink-0 ${
            dragActive 
              ? 'text-purple-500' 
              : 'text-gray-400 dark:text-gray-500'
          }`} />
          
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {dragActive ? 'Drop files here' : 'Add files'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              PNG, JPG, GIF, PDF, TXT, LOG • Max {MAX_FILES} files, 10MB each
            </p>
          </div>
          
          {files.length > 0 && (
            <div className="text-right">
              <div className="text-xs font-medium text-gray-900 dark:text-white">
                {files.length}/{MAX_FILES}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {formatFileSize(totalSize)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Compact File List */}
      {files.length > 0 && (
        <div className="max-h-32 overflow-y-auto space-y-1 bg-gray-50 dark:bg-gray-800 rounded-lg p-2">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center gap-2 p-2 bg-white dark:bg-gray-700 rounded text-xs"
            >
              <div className="flex-shrink-0 text-gray-500 dark:text-gray-400">
                {getFileIcon(file)}
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate">
                  {file.name}
                </p>
                <p className="text-gray-500 dark:text-gray-400">
                  {formatFileSize(file.size)}
                </p>
              </div>
              
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
                disabled={disabled}
                className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 
                         transition-colors disabled:opacity-50 disabled:cursor-not-allowed rounded"
                aria-label={`Remove ${file.name}`}
              >
                <XMarkIcon className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Compact Error Display */}
      {((validationResult && !validationResult.isValid) || error) && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <ExclamationCircleIcon className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-1">
              {error && (
                <p className="text-sm text-red-800 dark:text-red-200 font-medium">{error}</p>
              )}
              
              {validationResult && !validationResult.isValid && (
                <>
                  {validationResult.errors.map((errorMsg, index) => (
                    <p key={index} className="text-sm text-red-700 dark:text-red-300">
                      {errorMsg}
                    </p>
                  ))}
                  
                  {validationResult.invalidFiles.map((invalidFile, index) => (
                    <p key={index} className="text-sm text-red-700 dark:text-red-300">
                      <span className="font-medium">{invalidFile.file.name}:</span> {invalidFile.error}
                    </p>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Compact Size Warning */}
      {totalSize > 0 && remainingSize < 5 * 1024 * 1024 && (
        <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
          <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
          <span>
            {remainingSize > 0 
              ? `${formatFileSize(remainingSize)} remaining`
              : 'Size limit reached'
            }
          </span>
        </div>
      )}
    </div>
  );
};

export default FileAttachment;